/**
 * Authentication module for the Outlook Add-in.
 *
 * Primary: Office SSO → exchange for Ambient JWT.
 * Fallback: Dialog-based login via the Command Center app.
 *
 * Uses storage abstraction (localStorage or OfficeRuntime.Storage) so that
 * event-based activation on Windows New Outlook works — localStorage is
 * undefined there (OfficeDev/office-js#1977).
 */

import { config } from './config';
import { getItem, setItem, removeItem, storageKeys } from './storage';

interface TokenCache {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

/**
 * Get a valid Ambient access token, refreshing or re-authenticating as needed.
 *
 * @param options.allowDialog - When false (event-based runtimes), skips dialog auth
 *   since Office.context.ui.displayDialogAsync is not available there. Defaults to true.
 */
export async function getAccessToken(options?: { allowDialog?: boolean }): Promise<string> {
  const allowDialog = options?.allowDialog ?? true;
  const cached = await loadTokenCache();

  // Return cached token if still valid (with 60s buffer)
  if (cached && cached.expiresAt > Date.now() + 60_000) {
    return cached.accessToken;
  }

  // Try refresh token first (silent, no UI)
  if (cached?.refreshToken) {
    const refreshed = await tryRefreshToken(cached.refreshToken);
    if (refreshed) return refreshed;
  }

  // Try silent SSO first (no prompt) — uses the logged-in M365 session
  try {
    return await authenticateWithOfficeSso(false, /* requireReAuth */ false);
  } catch (ssoErr) {
    const code = (ssoErr as { code?: number })?.code;
    console.warn('[Ambient] Silent SSO failed', code ?? ssoErr);
  }

  // Event-based activation: never show prompts or dialogs.
  // If silent auth fails with no cached token, fail here so the resolver falls
  // back to a stale cached signature rather than interrupting the user.
  if (!allowDialog) {
    throw new Error('No credentials available. Open the Ambient Signature taskpane to sign in.');
  }

  // Taskpane context: allow sign-in prompt then dialog fallback.
  return authenticateWithOfficeSso(true, /* requireReAuth */ true);
}

/**
 * Get Office SSO token. Uses OfficeRuntime.auth in event-based activation
 * (Office.auth is blocked there per event-based activation docs).
 *
 * @param allowSignInPrompt - When true, Office will show a sign-in prompt if the user
 *   needs to re-authenticate (e.g. after signing out). Use true when we have no
 *   cached Ambient tokens so the user can sign in again.
 */
async function getOfficeSsoToken(allowSignInPrompt = false): Promise<string> {
  // Event-based activation: Office.auth is blocked. Use OfficeRuntime.auth.
  const g = globalThis as unknown as { OfficeRuntime?: { auth?: { getAccessToken: (opts?: object) => Promise<string> } } };
  if (typeof globalThis !== 'undefined' && g.OfficeRuntime?.auth) {
    const auth = g.OfficeRuntime.auth;
    return auth.getAccessToken({
      allowSignInPrompt,
      allowConsentPrompt: allowSignInPrompt,
      forMSGraphAccess: false,
    });
  }
  return Office.auth.getAccessToken({
    allowSignInPrompt,
    allowConsentPrompt: allowSignInPrompt,
    forMSGraphAccess: false,
  });
}

/**
 * Attempt Office SSO → Ambient JWT exchange.
 * Falls back to dialog auth only when allowDialog is true (taskpane context).
 * Event-based handlers must pass allowDialog=false — dialog API unavailable there.
 *
 * @param requireReAuth - When true, allow Office to show a sign-in prompt so the user
 *   can re-authenticate after signing out. Use when we have no cached Ambient tokens.
 */
async function authenticateWithOfficeSso(allowDialog: boolean, requireReAuth = false): Promise<string> {
  let officeToken: string;
  try {
    officeToken = await getOfficeSsoToken(requireReAuth);
  } catch (ssoErr) {
    // Office SSO token acquisition itself failed (not signed in, consent needed, etc.)
    const code = (ssoErr as { code?: number })?.code;
    console.warn('[Ambient] Office SSO token acquisition failed', code ?? ssoErr);
    if (!allowDialog) {
      throw new Error('Authentication required. Open the Ambient Signature taskpane to sign in.');
    }
    // Only fall back to dialog when the Office SSO token cannot be obtained at all.
    return authenticateWithDialog();
  }

  // We have a valid Office SSO token — exchange it for an Ambient JWT.
  // If the exchange fails, propagate the error instead of falling to dialog
  // (the Office token was valid, so the issue is on the backend side).
  const response = await fetch(`${config.apiBaseUrl}/auth/office-sso/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ officeToken }),
    signal: AbortSignal.timeout(10_000),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    const msg = `SSO exchange failed: ${response.status}${body ? ` — ${body}` : ''}`;
    console.error('[Ambient]', msg);
    throw new Error(msg);
  }

  const data = await response.json() as {
    accessToken: string;
    refreshToken: string;
    expiresAt: string;
    warnings?: string[];
  };

  if (data.warnings?.includes('no_role_assigned')) {
    console.warn('[Ambient] User authenticated but has no role assigned. Contact your admin to get permissions.');
  }

  await saveTokenCache({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    expiresAt: new Date(data.expiresAt).getTime(),
  });

  return data.accessToken;
}

/** Open a dialog to the Ambient login page.
 * Must stay on addin domain — redirecting to app domain loses Office.context.ui (messageParent).
 */
function authenticateWithDialog(): Promise<string> {
  return new Promise((resolve, reject) => {
    const loginUrl = `${config.addinUrl}/auth/outlook-addin-login.html`;

    Office.context.ui.displayDialogAsync(
      loginUrl,
      { height: 60, width: 30 },
      (asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          const err = asyncResult.error as { code?: number; message?: string } | undefined;
          const msg = err?.code
            ? `Failed to open login dialog (code ${err.code}${err.message ? `: ${err.message}` : ''})`
            : 'Failed to open login dialog';
          reject(new Error(msg));
          return;
        }

        const dialog = asyncResult.value;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg: { message?: string; origin?: string; error?: number }) => {
            if (typeof arg.message !== 'string') {
              dialog.close();
              reject(new Error('Invalid login dialog response'));
              return;
            }

            try {
              const data = JSON.parse(arg.message) as {
                accessToken: string;
                refreshToken: string;
                expiresAt: string;
              };

              void saveTokenCache({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiresAt: new Date(data.expiresAt).getTime(),
              });

              dialog.close();
              resolve(data.accessToken);
            } catch (err) {
              dialog.close();
              reject(err);
            }
          },
        );

        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          () => {
            reject(new Error('Login dialog was closed'));
          },
        );
      },
    );
  });
}

/** Try to refresh using an existing refresh token. */
async function tryRefreshToken(refreshToken: string): Promise<string | null> {
  try {
    const response = await fetch(`${config.apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      signal: AbortSignal.timeout(10_000),
    });

    if (!response.ok) return null;

    const data = await response.json() as {
      accessToken: string;
      refreshToken: string;
      expiresAt: string;
    };

    await saveTokenCache({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresAt: new Date(data.expiresAt).getTime(),
    });

    return data.accessToken;
  } catch {
    return null;
  }
}

async function saveTokenCache(cache: TokenCache): Promise<void> {
  await setItem(storageKeys.accessToken, cache.accessToken);
  await setItem(storageKeys.refreshToken, cache.refreshToken);
  await setItem(storageKeys.tokenExpiry, String(cache.expiresAt));
}

async function loadTokenCache(): Promise<TokenCache | null> {
  const accessToken = await getItem(storageKeys.accessToken);
  const refreshToken = await getItem(storageKeys.refreshToken);
  const expiresAt = await getItem(storageKeys.tokenExpiry);

  if (!accessToken || !refreshToken || !expiresAt) return null;

  return {
    accessToken,
    refreshToken,
    expiresAt: Number(expiresAt),
  };
}

/** Clear all cached tokens. */
export async function clearTokenCache(): Promise<void> {
  await removeItem(storageKeys.accessToken);
  await removeItem(storageKeys.refreshToken);
  await removeItem(storageKeys.tokenExpiry);
}
